import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Download } from 'lucide-react';
import { gstAPI } from '../../services/api';
import { useGst } from './GstContext';
import { inr, msg, today } from './gstUi';

const SHEETS = [
  ['b2b', 'B2B (registered buyers)', ['gstin', 'party', 'number', 'date', 'invoiceValue', 'rate', 'taxable', 'cgst', 'sgst', 'igst']],
  ['b2cs', 'B2C (unregistered), net of notes', ['placeOfSupply', 'rate', 'taxable', 'cgst', 'sgst', 'igst']],
  ['cdnr', 'Credit/debit notes to registered buyers', ['gstin', 'noteType', 'number', 'date', 'invoiceNumber', 'rate', 'taxable', 'cgst', 'sgst', 'igst']],
  ['hsn', 'HSN summary (net)', ['hsn', 'unit', 'rate', 'qty', 'taxable', 'cgst', 'sgst', 'igst']],
];
const MONEY = new Set(['invoiceValue', 'taxable', 'cgst', 'sgst', 'igst', 'noteValue']);

export default function GstReportsTab() {
  const { call, callRaw } = useGst();
  const [month, setMonth] = useState(today().slice(0, 7));
  const [rep, setRep] = useState(null);

  const load = useCallback(() => {
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    call(() => gstAPI.report(month)).then((b) => setRep(b.data)).catch((e) => toast.error(msg(e)));
  }, [call, month]);
  useEffect(() => { load(); }, [load]);

  const download = async (params, name, kind) => {
    try {
      const r = await callRaw(() => (kind === 'ca' ? gstAPI.downloadCaExcel(month) : gstAPI.exportReport({ month, ...params })));
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) { toast.error(e.response?.status === 423 ? 'Locked' : 'Export failed'); }
  };

  const s = rep?.summary;
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <input className="form-control" style={{ maxWidth: 170 }} type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <button className="btn btn-primary" onClick={() => download({ format: 'xlsx' }, `gst-report-${month}.xlsx`)}><Download size={14} /> Excel (all sheets)</button>
        <button className="btn btn-primary" onClick={() => download({}, `GSTR1-${month}.xlsx`, 'ca')}><Download size={14} /> CA Excel (GSTR-1 format)</button>
      </div>
      {s && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {[['Invoices', s.invoiceCount], ['Credit notes', s.creditNoteCount], ['Debit notes', s.debitNoteCount],
            ['Taxable value', `₹${inr(s.taxable)}`], ['CGST', `₹${inr(s.cgst)}`], ['SGST', `₹${inr(s.sgst)}`], ['IGST', `₹${inr(s.igst)}`], ['Total', `₹${inr(s.total)}`]]
            .map(([k, v]) => <div key={k} className="card" style={{ padding: '8px 14px' }}><div style={{ fontSize: 11, opacity: 0.7 }}>{k}</div><b>{v}</b></div>)}
        </div>
      )}
      {rep && SHEETS.map(([key, title, cols]) => (
        <div key={key} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ margin: '6px 0' }}>{title}</h4>
            <button className="btn btn-secondary" onClick={() => download({ format: 'csv', sheet: key }, `gst-${key}-${month}.csv`)}><Download size={14} /> CSV</button>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead><tr>{cols.map((c) => <th key={c} style={{ textAlign: 'left', padding: 4 }}>{c}</th>)}</tr></thead>
              <tbody>
                {rep[key].map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid rgba(128,128,128,.25)' }}>
                    {cols.map((c) => <td key={c} style={{ padding: 4 }}>{MONEY.has(c) ? inr(r[c]) : r[c] ?? ''}</td>)}
                  </tr>
                ))}
                {rep[key].length === 0 && <tr><td colSpan={cols.length} style={{ padding: 8, opacity: 0.6 }}>No rows this month.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}
